/** Route-owned lifetime of the Resource editor screens (`/coach/resources/new`
 * and `/coach/resources/$resourceId`). The session core owns commands, receipts
 * and recovery; this hook owns which session is mounted, the URL after the first
 * accepted save, kind tabs and the read-only states. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useComposerDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import { isArchivedTeam } from '@/features/navigation/seasonGroups';
import { ApiError, getCoachResource, getCoachResourceRecording, getCoachResourceRevision } from '@/lib/api';
import { emptyScene } from '@/lib/canvas/board-schema';
import { loadResourceEditorContinuation, resourceEditorContinuationKind,
  type ResourceEditorReturnTarget } from '@/lib/resourceEditorContinuation';
import type { ResourceDetailResponse, ResourceKind, ResourceRecordingDescriptor } from '@/lib/types';
import { useScopeStore } from '@/stores/scope';
import { createResourceBoardSession, type ResourceBoardSession } from './resourceEditorSession';
import type { ResourceEditorLinkTarget } from './resourceEditorSessionCore';
import { createResourceVideoSession, emptyResourceVideo, type ResourceVideoSession,
  type ResourceVideoValue } from './resourceVideoSession';
import { seedClipWindow } from './resourceClipWindow';
import { useResourceBoardSession } from './useResourceBoardSession';

export type ResourceEditorSlot = ResourceRecordingDescriptor['slot'];
export type ResourceEditorRoute =
  | { mode:'new'; kind?:ResourceKind; matchId?:string; slot?:ResourceEditorSlot; entry?:string }
  | { mode:'saved'; resourceId:string; entry?:string; revision?:string };
/** Where the page should be, applied with `replace` so a reload reopens it. */
export type ResourceEditorLocation =
  | { mode:'new'; kind:ResourceKind; matchId?:string; slot?:ResourceEditorSlot; entry:string }
  | { mode:'saved'; resourceId:string; entry:string; revision?:string };
export type ResourceEditorSession = ResourceBoardSession | ResourceVideoSession;
export type ResourceReadOnly = 'team-archived' | 'archived' | 'older-version' | 'other-coach';
type Mounted = { entryId:string; kind:ResourceKind; session:ResourceEditorSession };
type MatchPick = { matchId:string; slot:ResourceEditorSlot };
type Phase =
  | { status:'choose'; kind?:ResourceKind }
  | { status:'loading' }
  | { status:'error'; reason:'unavailable' | 'failed' }
  | { status:'ready' };

const errorCode = (error:unknown) => error instanceof ApiError && error.detail && typeof error.detail === 'object'
  && 'code' in error.detail ? String(error.detail.code) : null;
/** A command whose result is not settled yet: in flight, unknown or rejected. */
const unsettled = (mounted:Mounted | undefined | null) => {
  const value = mounted?.session.getState();
  return !!value && value.status !== 'unavailable' && !!value.command && value.command.status !== 'accepted';
};
const nextAttempt = (target:ResourceEditorReturnTarget):ResourceEditorReturnTarget =>
  target.kind === 'feedback' ? { ...target, creation_attempt_id:crypto.randomUUID() } : target;

function mount(context:DraftContext, entryId:string, kind:ResourceKind, target:ResourceEditorReturnTarget,
  initial:{ detail:ResourceDetailResponse } | { video?:ResourceVideoValue }):Mounted {
  const saved = 'detail' in initial ? { kind:'saved' as const, detail:initial.detail } : null;
  const session = kind === 'tactical_board'
    ? createResourceBoardSession(context, entryId, saved ?? { kind:'new', value:{ scene:emptyScene(), label:'' } }, target)
    : createResourceVideoSession(context, entryId, kind,
      saved ?? { kind:'new', value:('video' in initial && initial.video) || emptyResourceVideo(kind) }, target);
  return { entryId, kind, session };
}

/** Which read-only state applies, most specific first. A save in flight moves
 * `origin` ahead of the authority read, so wait for the refresh to decide. */
export function resourceReadOnly(state:ReturnType<ResourceEditorSession['getState']> | null,
  teamArchived:boolean):ResourceReadOnly | null {
  if (teamArchived) return 'team-archived';
  if (state?.status !== 'available' || !state.authority?.editor || state.refreshRequired) return null;
  const { resource, editor } = state.authority;
  if (resource.archived) return 'archived';
  if (state.origin && editor.current_revision_id !== state.origin.revision_id) return 'older-version';
  return editor.can_edit ? null : 'other-coach';
}

export function useResourceEditor(route:ResourceEditorRoute, onLocation:(location:ResourceEditorLocation) => void) {
  const context = useComposerDraftContext();
  const team = useScopeStore(state => state.active?.team);
  const [phase, setPhase] = useState<Phase>({ status:'loading' });
  const [mounted, setMounted] = useState<Mounted | null>(null);
  const [teamArchivedWrite, setTeamArchivedWrite] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [recording, setRecording] = useState<ResourceRecordingDescriptor | null>(null);
  /** The last refused command's error code, until recovered. */
  const [failure, setFailure] = useState<string | null>(null);
  // Every session this page created; the other family stays parked across tabs (F3).
  const owned = useRef(new Map<ResourceKind | 'video', Mounted>());
  // F2/F3: a saved video Resource a tab switch replaced, kept until it is
  // pinned with the rest ("Returning to the draft pins both").
  const settled = useRef<Mounted[]>([]);
  const target = useRef<ResourceEditorReturnTarget>({ kind:'standalone' });
  const flush = useRef<(() => void) | null>(null);
  const location = useRef(onLocation); location.current = onLocation;
  const current = useRef<Mounted | null>(null); current.current = mounted;
  const state = useResourceBoardSession(mounted?.session ?? null);
  const routeKey = route.mode === 'new'
    ? `new:${route.kind ?? ''}:${route.matchId ?? ''}:${route.slot ?? ''}:${route.entry ?? ''}`
    : `saved:${route.resourceId}:${route.entry ?? ''}:${route.revision ?? ''}`;
  const contextKey = context ? `${context.actorId}:${context.clubId}:${context.teamId}:${context.seasonId}:${context.generation}` : '';

  const show = useCallback((next:Mounted) => {
    const family = next.kind === 'tactical_board' ? 'tactical_board' : 'video';
    const replaced = owned.current.get(family);
    if (replaced && replaced !== next) {
      const kept = replaced.session.getState();
      if (kept.status === 'available' && kept.receipt && replaced.session.getReturnTarget()?.kind === 'feedback') {
        replaced.session.setActive(false); settled.current.push(replaced);
      } else replaced.session.dispose();
    }
    owned.current.set(family, next); current.current = next;
    for (const [key, other] of owned.current) if (key !== family) other.session.setActive(false);
    next.session.setActive(true);
    setMounted(next);
  }, []);

  /** Forget every session and the page state that belongs to one Resource. */
  const release = useCallback(() => {
    for (const kept of [...owned.current.values(), ...settled.current]) kept.session.dispose();
    owned.current.clear(); settled.current = []; current.current = null; flush.current = null;
    setMounted(null); setTeamArchivedWrite(false); setSavedAt(null); setRecording(null); setFailure(null);
  }, []);
  useEffect(() => () => {
    for (const kept of [...owned.current.values(), ...settled.current]) kept.session.dispose();
    owned.current.clear(); settled.current = [];
  }, []);
  // A scope or identity change retires every session (the core fences it too).
  useEffect(() => release, [contextKey, release]);

  useEffect(() => {
    if (!context) { setPhase({ status:'loading' }); return; }
    const live = current.current;
    // The /new → /$id replace and a duplicate keep this page's session; only a
    // different entry, Resource or pinned version starts over.
    if (live && route.entry === live.entryId && live.session.getState().status !== 'unavailable') {
      const origin = (live.session.getState() as { origin?:{ resource_id:string; revision_id:string } | null }).origin;
      if (route.mode === 'new' ? !origin : origin?.resource_id === route.resourceId
        && (!route.revision || route.revision === origin.revision_id)) return;
    }
    const abort = new AbortController();
    const entryId = route.entry ?? crypto.randomUUID();
    if (live) release();
    if (!route.entry) {
      if (route.mode === 'new' && route.kind) location.current({ mode:'new', kind:route.kind, matchId:route.matchId, slot:route.slot, entry:entryId });
      if (route.mode === 'saved') location.current({ mode:'saved', resourceId:route.resourceId, entry:entryId, revision:route.revision });
      if (route.mode === 'saved' || route.kind) return;
    }
    let row: ReturnType<typeof loadResourceEditorContinuation>;
    try { row = loadResourceEditorContinuation(context, entryId); } catch { setPhase({ status:'error', reason:'failed' }); return; }
    target.current = row?.return_target ?? { kind:'standalone' };
    void (async () => {
      try {
        if (route.mode === 'new') {
          const accepted = row?.accepted?.receipt;
          // Reloaded after an accepted create: reopen that Resource, not a blank editor.
          if (accepted && !row?.pending) { location.current({ mode:'saved', resourceId:accepted.resource_id, entry:entryId }); return; }
          const kind = row ? resourceEditorContinuationKind(row) : route.kind;
          if (!kind) { setPhase({ status:'choose' }); return; }
          const video = kind !== 'tactical_board';
          if (video && !row?.pending && (!route.matchId || !route.slot)) { setPhase({ status:'choose', kind }); return; }
          setPhase({ status:'loading' });
          // The recording is part of the blank value, so a fresh editor is not dirty.
          let seed:ResourceVideoValue | undefined;
          if (video && !row?.pending && route.matchId && route.slot) {
            const source = await getCoachResourceRecording(route.matchId, route.slot, { scope:context, signal:abort.signal });
            if (abort.signal.aborted) return;
            seed = { ...emptyResourceVideo(kind), recording:source };
            // The recording's length is unknown here; the clip stage bounds it.
            if (kind === 'clip') seed.window = seedClipWindow(0, 0);
            setRecording(source);
          }
          show(mount(context, entryId, kind, target.current, { video:seed })); setPhase({ status:'ready' });
          return;
        }
        setPhase({ status:'loading' });
        const selected = row?.pending ? row.origin : row?.accepted?.receipt ?? null;
        const pin = route.revision ? { resource_id:route.resourceId, revision_id:route.revision }
          : selected?.resource_id === route.resourceId ? selected : null;
        const detail = pin ? await getCoachResourceRevision(pin, { scope:context, signal:abort.signal })
          : await getCoachResource(route.resourceId, { scope:context, signal:abort.signal });
        if (abort.signal.aborted) return;
        if (detail.resource.availability !== 'available' || !detail.editor) { setPhase({ status:'error', reason:'unavailable' }); return; }
        if (row && resourceEditorContinuationKind(row) !== detail.resource.kind) { setPhase({ status:'error', reason:'failed' }); return; }
        const next = mount(context, entryId, detail.resource.kind, target.current, { detail });
        const restored = next.session.getState();
        if (restored.status === 'available' && 'recording' in restored.value) setRecording(restored.value.recording);
        show(next); setPhase({ status:'ready' });
        if (restored.status === 'available' && restored.refreshRequired && !restored.command) void settle(next).catch(() => {});
      } catch (error) {
        if (abort.signal.aborted) return;
        setPhase({ status:'error', reason:error instanceof ApiError && [403, 404].includes(error.status) ? 'unavailable' : 'failed' });
      }
    })();
    return () => abort.abort();
    // routeKey/contextKey carry the route and scope identity.
  }, [routeKey, contextKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fail = (error:unknown) => {
    const code = errorCode(error);
    if (code === 'resource_team_archived') setTeamArchivedWrite(true);
    setFailure(code ?? 'failed');
    throw error;
  };
  const session = mounted?.session ?? null;

  /** A fresh read, then the root version a Library rename, archive or restore
   * moved (finding: a stale CAS was otherwise never recoverable). */
  async function settle(live:Mounted) {
    await live.session.refresh();
    live.session.adoptCurrentVersion();
  }
  /** Commands need a settled read; a failed one is retried first. Without one
   * to retry, the command is captured in the same tick (tabs lock at once). */
  function prepare(live:Mounted):Promise<void> | null {
    setFailure(null);
    const value = live.session.getState();
    return value.status === 'available' && value.refreshRequired && !value.refreshing && !value.command ? settle(live) : null;
  }
  async function commit(live:Mounted, options:{ stay?:boolean; publicationState?:'draft' | 'published' }) {
    if (live === current.current) flush.current?.();
    { const reading = prepare(live); if (reading) await reading; }
    const created = !(live.session.getState() as { origin?:unknown }).origin;
    const receipt = await live.session.save(undefined, created ? { publicationState:options.publicationState ?? 'draft' } : {}).catch(fail);
    setSavedAt(new Date());
    if (created && !options.stay && current.current === live) {
      location.current({ mode:'saved', resourceId:receipt.resource_id, entry:live.entryId });
    }
    await settle(live).catch(() => {});
    return receipt;
  }
  /** Save draft (F1 step 5). A new Resource is created as a draft; the first
   * accepted save replaces `/new` with `/$id` unless the page is leaving. */
  const save = useCallback(async (options:{ publicationState?:'draft' | 'published'; stay?:boolean } = {}) => {
    const live = current.current; if (!live) throw new Error('resource_editor_unavailable');
    return commit(live, options);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Publish (F1 step 6): save first when there are changes, then the one-way
   * publish command. Inside a feedback entry only one command can be accepted,
   * so an unsaved Resource there is created published instead. */
  const publish = useCallback(async () => {
    const live = current.current; if (!live) throw new Error('resource_editor_unavailable');
    flush.current?.();
    const value = live.session.getState();
    if (value.status !== 'available') throw new Error('resource_editor_unavailable');
    if (!value.origin && target.current.kind !== 'standalone') return save({ publicationState:'published' });
    if (!value.origin || value.dirty) await save();
    { const reading = prepare(live); if (reading) await reading; }
    const ready = live.session.getState();
    if (ready.status === 'available' && ready.authority?.resource.publication_state === 'published') return null;
    const receipt = await live.session.publish().catch(fail);
    await settle(live).catch(() => {});
    return receipt;
  }, [save]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicate = useCallback(async () => {
    const live = current.current; if (!live) throw new Error('resource_editor_unavailable');
    { const reading = prepare(live); if (reading) await reading; }
    const receipt = await live.session.duplicate().catch(fail);
    location.current({ mode:'saved', resourceId:receipt.resource_id, entry:live.entryId });
    await settle(live).catch(() => {});
    return receipt;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const archive = useCallback(async () => {
    const live = current.current; if (!live) throw new Error('resource_editor_unavailable');
    { const reading = prepare(live); if (reading) await reading; }
    await live.session.archive().catch(fail);
    await settle(live).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** "Check again": release a refused command (its edits stay), then a fresh
   * read and version. A newer revision becomes the older-version state. */
  const recover = useCallback(async () => {
    const live = current.current; if (!live) return;
    const value = live.session.getState();
    if (value.status === 'available' && value.command?.status === 'rejected') live.session.releaseRejected();
    await settle(live);
    setFailure(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** The video session a switch to `kind` would replace and its edits drop. */
  const replacedBy = (kind:ResourceKind) => {
    const video = owned.current.get('video');
    if (kind === 'tactical_board' || !video || video.kind === kind) return null;
    const value = video.session.getState();
    return value.status === 'available' && value.dirty ? video : null;
  };
  /** Tabs stay put while the shown or the replaced Resource has an unsettled command. */
  const canSwitch = (kind:ResourceKind) => {
    const live = current.current, value = live?.session.getState();
    if (!live || live.kind === kind || value?.status !== 'available' || value.origin || unsettled(live)) return false;
    const video = owned.current.get('video');
    return kind === 'tactical_board' || !video || video === live || video.kind === kind || !unsettled(video);
  };

  /** Kind tabs (F3) on an unsaved Resource. Moment ↔ Clip keeps the recording
   * and seeds from the playhead; the board and the video editor each stay
   * parked while the other shows. A feedback entry gets a new attempt. Returns
   * `'confirm'` when unsaved edits need Save draft or Discard first, and
   * `'choose'` when a video kind has no recording to open yet. */
  const switchKind = useCallback(async (kind:ResourceKind, at:{ playhead:number; duration:number } = { playhead:0, duration:0 },
    choice?:'save' | 'discard', pick?:MatchPick):Promise<'confirm' | 'choose' | 'switched' | 'ignored'> => {
    const live = current.current;
    if (!context || !live || !canSwitch(kind)) return 'ignored';
    const family = kind === 'tactical_board' ? 'tactical_board' : 'video';
    const parked = owned.current.get(family);
    let seed:ResourceVideoValue | null = null;
    if (kind !== 'tactical_board' && !(parked && parked.kind === kind)) {
      const from = parked ?? live, previous = from.session.getState();
      const source = previous.status === 'available' && 'recording' in previous.value ? previous.value : null;
      // A board reopened from its URL knows the match only from the route.
      const where = pick ?? (route.mode === 'new' && route.matchId && route.slot ? { matchId:route.matchId, slot:route.slot } : null);
      const descriptor = (pick ? null : source?.recording ?? recording)
        ?? (where ? await getCoachResourceRecording(where.matchId, where.slot, { scope:context }) : null);
      if (!descriptor) return 'choose';
      setRecording(descriptor);
      // From the board, seed from the parked video's own position, not the board.
      const playhead = from === live ? at.playhead
        : source?.kind === 'moment' ? source.timestampSeconds : source?.window.start ?? 0;
      seed = emptyResourceVideo(kind);
      seed.recording = descriptor;
      if (kind === 'clip') seed.window = seedClipWindow(Math.max(0, playhead), from === live ? at.duration : 0);
      else seed.timestampSeconds = Math.max(0, playhead);
    }
    const replaced = replacedBy(kind);
    if (replaced && !choice) return 'confirm';
    if (replaced && choice === 'save') {
      replaced.session.setActive(true);
      // F2: the saved receipt stays with its feedback entry; this page stays.
      await commit(replaced, { stay:true });
    }
    const next = parked && parked.kind === kind ? parked
      : mount(context, crypto.randomUUID(), kind, nextAttempt(target.current), seed ? { video:seed } : {});
    show(next);
    const shown = next.session.getState();
    const descriptor = shown.status === 'available' && 'recording' in shown.value ? shown.value.recording : null;
    location.current({ mode:'new', kind, entry:next.entryId,
      matchId:descriptor?.match_id ?? (route.mode === 'new' ? route.matchId : undefined),
      slot:descriptor?.slot ?? (route.mode === 'new' ? route.slot : undefined) });
    return 'switched';
  }, [context, recording, route, show]); // eslint-disable-line react-hooks/exhaustive-deps

  const teamArchived = isArchivedTeam(team) || teamArchivedWrite;
  /** Sessions with edits a departure would drop: the shown one and a parked
   * one (F3). An archived team's edits can no longer be saved, so none count. */
  const unsaved = () => teamArchived ? [] : [...owned.current.values()].filter(kept => {
    const value = kept.session.getState();
    return value.status === 'available' && value.dirty && value.canEdit;
  });
  /** Whether a location is one of this page's own editor entries (its
   * `/new` → `/$id` replace, a kind tab), which keeps every session. */
  const ownsEntry = (entry:string | undefined) => !!entry && [...owned.current.values()].some(kept => kept.entryId === entry);
  /** Save every unsaved session as a draft, the shown one first. */
  const saveUnsaved = useCallback(async () => {
    for (const kept of unsaved().sort((a, b) => Number(b === current.current) - Number(a === current.current))) {
      kept.session.setActive(true);
      try { await commit(kept, { stay:true }); }
      finally { if (kept !== current.current && kept.session.getState().status !== 'unavailable') kept.session.setActive(false); }
    }
  }, [teamArchived]); // eslint-disable-line react-hooks/exhaustive-deps
  /** Drop the unsaved edits of every session (a parked one is woken to do it). */
  const discardUnsaved = useCallback(() => {
    for (const kept of unsaved()) {
      kept.session.setActive(true);
      try { kept.session.discardChanges(); } catch { /* An unsettled command keeps its own recovery. */ }
      if (kept !== current.current) kept.session.setActive(false);
    }
  }, [teamArchived]); // eslint-disable-line react-hooks/exhaustive-deps
  /** F2: the saved Resources of this feedback entry not yet pinned to it. */
  const unpinned = () => [...owned.current.values(), ...settled.current].map(kept => kept.session).filter(live => {
    const value = live.getState();
    return value.status === 'available' && !!value.receipt && live.getReturnTarget()?.kind === 'feedback'
      && value.linkOutcome?.status !== 'linked' && value.linkOutcome?.status !== 'already-linked';
  });
  /** F2 steps 3–5: pin each saved Resource to the reopened draft with its own
   * creation attempt (admission dedupes). Returns the first refusal, if any. */
  const pinAll = useCallback(async (destination:ResourceEditorLinkTarget) => {
    for (const live of unpinned()) {
      live.setActive(true);
      try {
        const outcome = await live.returnToFeedback(destination);
        if (outcome.status === 'retained') return outcome;
      } finally { if (live.getState().status !== 'unavailable') live.setActive(false); }
    }
    return null;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** The moment stage hands its live playhead in before any save. */
  const setFlush = useCallback((fn:(() => void) | null) => { flush.current = fn; }, []);
  const readOnly = resourceReadOnly(state, teamArchived);
  return {
    phase, context, mounted, session, state, readOnly, savedAt, failure, target:target.current,
    save, publish, duplicate, archive, recover, switchKind, setFlush, canSwitch,
    unsaved, ownsEntry, saveUnsaved, discardUnsaved, unpinned, pinAll,
    /** The kind whose unsaved edits a switch to `kind` would drop (F3 asks first). */
    replacing:(kind:ResourceKind) => replacedBy(kind)?.kind ?? null,
  };
}
export type ResourceEditorController = ReturnType<typeof useResourceEditor>;
